/**
 * Admin — Partner deliveries
 *
 * Which published links are delivered to which learning provider. A token a
 * provider is not bound to is a 404 in their partner API; binding it makes the
 * token and every watch already recorded on it visible to them at once.
 * Binding is deliberate post-processing: paste a token, see what it is, bind.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Link2, Link2Off, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

export const Route = createFileRoute('/admin/deliveries')({
  component: AdminDeliveries,
});

interface Provider {
  key: string;
  actorHomePage: string;
  allowedRedirectDomains: string[];
  requireSignature: boolean;
  pullEnabled: boolean;
  boundShares: number;
}

interface Share {
  shareToken: string;
  kind: 'video' | 'assessment';
  conversationId: string;
  title: string | null;
  ownerEmail: string | null;
  providerKey: string | null;
  isActive: boolean;
  revokedAt: string | null;
  createdAt: string;
  views: number;
  identifiedLearners: number;
  lastViewedAt: string | null;
}

interface UnboundTraffic {
  shareToken: string;
  kind: 'video' | 'assessment';
  title: string | null;
  matchedProviderKey: string;
  embedOrigin: string;
  identifiedViews: number;
  views: number;
  lastViewedAt: string;
}

const PLAY_BASE = 'https://play.4klabs.ai';

function playUrl(s: { shareToken: string; kind: 'video' | 'assessment' }): string {
  return s.kind === 'video' ? `${PLAY_BASE}/${s.shareToken}/` : `${PLAY_BASE}/a/${s.shareToken}/`;
}

function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
}

function AdminDeliveries() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerKey, setProviderKey] = useState<string>('');
  const [shares, setShares] = useState<Share[]>([]);
  const [unbound, setUnbound] = useState<UnboundTraffic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tokenInput, setTokenInput] = useState('');
  const [resolved, setResolved] = useState<Share | null>(null);
  const [resolving, setResolving] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadProviders = useCallback(async () => {
    const r = await api.get('/admin/providers');
    const list: Provider[] = r.data.providers;
    setProviders(list);
    setProviderKey((k) => k || list[0]?.key || '');
  }, []);

  const loadShares = useCallback(async (key: string) => {
    if (!key) { setShares([]); return; }
    const r = await api.get(`/admin/providers/${encodeURIComponent(key)}/shares`);
    setShares(r.data.shares);
  }, []);

  const loadUnbound = useCallback(async () => {
    const r = await api.get('/admin/deliveries/unbound');
    setUnbound(r.data.unbound);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadProviders(), loadUnbound()]);
    } catch (err) {
      setError(errorText(err, 'Could not load deliveries'));
    } finally {
      setLoading(false);
    }
  }, [loadProviders, loadUnbound]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    loadShares(providerKey).catch((err) => setError(errorText(err, 'Could not load the delivery list')));
  }, [providerKey, loadShares]);

  async function resolveToken(token: string) {
    const t = token.trim();
    if (!t) { setResolved(null); return; }
    setResolving(true);
    setError(null);
    try {
      const r = await api.get(`/admin/shares/${encodeURIComponent(t)}`);
      setResolved(r.data.share);
    } catch (err) {
      setResolved(null);
      setError(errorText(err, 'No share with that token'));
    } finally {
      setResolving(false);
    }
  }

  async function setProvider(token: string, next: string | null, replace = false) {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/admin/shares/${encodeURIComponent(token)}/provider`, { providerKey: next, replace });
      await Promise.all([loadProviders(), loadShares(providerKey), loadUnbound()]);
      if (resolved?.shareToken === token) await resolveToken(token);
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e?.response?.status === 409 && !replace) {
        if (confirm(`${e.response.data?.error}\n\nMove it anyway? The other provider loses the token and every watch on it.`)) {
          setBusy(false);
          await setProvider(token, next, true);
          return;
        }
      }
      setError(errorText(err, 'Could not change the binding'));
    } finally {
      setBusy(false);
    }
  }

  async function unbind(s: { shareToken: string; providerKey: string | null; identifiedLearners?: number }) {
    if (!confirm(`Stop delivering ${s.shareToken} to "${s.providerKey}"? Their partner API will 404 for it and hide its watches until it is bound again.`)) return;
    await setProvider(s.shareToken, null);
  }

  const selected = providers.find((p) => p.key === providerKey);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Partner Deliveries</h1>
          <p className="text-muted-foreground">
            Which published links a learning provider can read through the partner API. Binding is retroactive — every watch already on the token becomes visible to them.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      {unbound.length > 0 && (
        <Card className="border-amber-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              Provider traffic on tokens that are not delivered to anyone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Identified learners are arriving from a provider's own domain on a token that is not bound to it. Their partner API is returning 404 for these right now.
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Arriving from</TableHead>
                  <TableHead>Looks like</TableHead>
                  <TableHead className="text-right">Identified views</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unbound.map((u) => (
                  <TableRow key={`${u.shareToken}|${u.embedOrigin}`}>
                    <TableCell className="font-mono text-xs">{u.shareToken}</TableCell>
                    <TableCell className="text-sm">{u.title ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.embedOrigin}</TableCell>
                    <TableCell><Badge variant="outline">{u.matchedProviderKey}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{u.identifiedViews}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(u.lastViewedAt).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" disabled={busy} onClick={() => setProvider(u.shareToken, u.matchedProviderKey)}>
                        <Link2 className="h-4 w-4 mr-1" /> Deliver to {u.matchedProviderKey}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Deliver a token</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[280px]">
              <Label>Share token</Label>
              <Input
                value={tokenInput}
                onChange={(e) => { setTokenInput(e.target.value); setResolved(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') resolveToken(tokenInput); }}
                placeholder="Sw7FLGfYGNrkISo0w5QJzw"
                className="font-mono"
              />
            </div>
            <Button variant="outline" onClick={() => resolveToken(tokenInput)} disabled={resolving || !tokenInput.trim()}>
              {resolving ? 'Looking up…' : 'Look up'}
            </Button>
          </div>

          {resolved && (
            <div className="mt-4 rounded border p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{resolved.kind}</Badge>
                <span className="font-medium">{resolved.title ?? 'Untitled'}</span>
                {!resolved.isActive && <Badge variant="secondary">revoked</Badge>}
              </div>
              <div className="text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                <span>Owner <b className="text-foreground">{resolved.ownerEmail ?? '—'}</b></span>
                <span>Published <b className="text-foreground">{new Date(resolved.createdAt).toLocaleDateString()}</b></span>
                <span>Views <b className="text-foreground tabular-nums">{resolved.views}</b></span>
                <span>Identified learners <b className="text-foreground tabular-nums">{resolved.identifiedLearners}</b></span>
                <span>
                  Delivered to{' '}
                  {resolved.providerKey
                    ? <Badge>{resolved.providerKey}</Badge>
                    : <Badge variant="secondary">nobody</Badge>}
                </span>
                <a className="underline" href={playUrl(resolved)} target="_blank" rel="noreferrer">open</a>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {providers.map((p) => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={resolved.providerKey === p.key ? 'default' : 'outline'}
                    disabled={busy || resolved.providerKey === p.key}
                    onClick={() => setProvider(resolved.shareToken, p.key)}
                  >
                    <Link2 className="h-4 w-4 mr-1" />
                    {resolved.providerKey === p.key ? `Delivered to ${p.key}` : `Deliver to ${p.key}`}
                  </Button>
                ))}
                {resolved.providerKey && (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => unbind(resolved)}>
                    <Link2Off className="h-4 w-4 mr-1 text-destructive" /> Stop delivering
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        {providers.map((p) => (
          <Button key={p.key} variant={p.key === providerKey ? 'default' : 'outline'} size="sm" onClick={() => setProviderKey(p.key)}>
            {p.key} <span className="ml-2 opacity-70 tabular-nums">{p.boundShares}</span>
          </Button>
        ))}
      </div>
      {selected && (
        <p className="text-sm text-muted-foreground -mt-3">
          {selected.actorHomePage}
          {selected.allowedRedirectDomains.length > 0 && <> · domains: {selected.allowedRedirectDomains.join(', ')}</>}
          {' · '}pull API {selected.pullEnabled ? 'enabled' : 'off'}
          {' · '}signatures {selected.requireSignature ? 'required' : 'not required'}
        </p>
      )}

      <Card>
        <CardHeader><CardTitle>Delivered to “{providerKey || '…'}”</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center min-h-[160px]">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Learners</TableHead>
                  <TableHead>Last viewed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shares.map((s) => (
                  <TableRow key={s.shareToken}>
                    <TableCell className="font-mono text-xs">
                      <a className="underline" href={playUrl(s)} target="_blank" rel="noreferrer">{s.shareToken}</a>
                    </TableCell>
                    <TableCell className="text-sm">{s.title ?? '—'}</TableCell>
                    <TableCell><Badge variant="outline">{s.kind}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.ownerEmail ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.views}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.identifiedLearners}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.lastViewedAt ? new Date(s.lastViewedAt).toLocaleString() : '—'}</TableCell>
                    <TableCell>
                      <Badge variant={s.isActive ? 'default' : 'secondary'}>{s.isActive ? 'active' : 'revoked'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => unbind(s)} title="Stop delivering">
                        <Link2Off className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {shares.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-8">
                      Nothing delivered to this provider yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
